import { useCallback, useRef, useState } from "react";

const DIRECT_UPLOAD_URL = "/rails/active_storage/direct_uploads";

interface UseDirectUploadResult {
  upload: (file: File) => Promise<string>;
  progress: number;
  error: string | null;
  signedId: string | null;
  isUploading: boolean;
}

export function useDirectUpload(): UseDirectUploadResult {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [signedId, setSignedId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Uploads may be superseded (pick file B while A is in flight): every call
  // settles its own promise, but only the latest call writes the shared
  // progress/error/isUploading state.
  const callIdRef = useRef(0);

  const upload = useCallback(async (file: File): Promise<string> => {
    const callId = ++callIdRef.current;
    const isCurrent = () => callIdRef.current === callId;

    const { DirectUpload } = await import("@rails/activestorage");

    if (isCurrent()) {
      setProgress(0);
      setError(null);
      setSignedId(null);
      setIsUploading(true);
    }

    return new Promise((resolve, reject) => {
      const directUpload = new DirectUpload(file, DIRECT_UPLOAD_URL, {
        directUploadWillStoreFileWithXHR(request: XMLHttpRequest) {
          request.upload.addEventListener("progress", (event: ProgressEvent) => {
            if (event.lengthComputable && isCurrent()) {
              setProgress(Math.round((event.loaded / event.total) * 100));
            }
          });
        },
      });

      directUpload.create((uploadError: Error, blob: { signed_id: string }) => {
        if (uploadError) {
          if (isCurrent()) {
            setError(uploadError.message);
            setIsUploading(false);
          }
          reject(uploadError);
        } else {
          if (isCurrent()) {
            setSignedId(blob.signed_id);
            setProgress(100);
            setIsUploading(false);
          }
          resolve(blob.signed_id);
        }
      });
    });
  }, []);

  return { upload, progress, error, signedId, isUploading };
}
